import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconLogin2, IconWifi, IconWifiOff } from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { APP_NAME, PLAYERS_COUNT_LABEL } from '@creativitas/shared';
import type { GameInvitationView, GameSessionView, GuestSessionCredentials } from '@creativitas/shared';
import { SessionStatusBadge } from '@/components/domain/SessionStatusBadge';
import { SwitchGuestButton } from '@/components/domain/SwitchGuestButton';
import { ErrorState, LoadingState } from '@/components/ui/StateViews';
import { SessionParticipants } from '@/features/spreads/SessionParticipants';
import { useSessionRealtime } from '@/features/spreads/useSessionRealtime';
import { BRAND } from '@/lib/assets';
import { toUserMessage } from '@/lib/api';
import {
  forgetGuestCredentials,
  isRevokedGuestAccess,
  loadGuestCredentials,
  saveGuestCredentials,
} from '@/lib/guest-credentials';
import {
  drawGuestGameCard,
  drawGuestGrantedTalent,
  drawGuestStartTalent,
  fetchGuestGameSession,
  joinGameSession,
  revealGuestGameCard,
  rollGuestGameDice,
} from './api';
import { SESSION_SURFACE_WIDTH } from '@/theme/tokens';
import { GameWorkspace } from './GameWorkspace';
import { newerSnapshot, roster } from './game-turn';

export function GuestGameSession({
  invitation,
  token,
}: {
  invitation: GameInvitationView;
  token: string;
}) {
  const queryClient = useQueryClient();
  /* Кем гость был в этой партии — помнит браузер, см. `guest-credentials`. */
  const [credentials, setCredentials] = useState<GuestSessionCredentials | null>(() => loadGuestCredentials(token));
  /* Почему снова форма имени, если прежний участник больше не действует. */
  const [notice, setNotice] = useState<string | null>(null);
  const key = ['guest-game-session', credentials?.sessionId ?? invitation.sessionId] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => fetchGuestGameSession(credentials!),
    enabled: Boolean(credentials),
    retry: false,
  });
  const realtime = useSessionRealtime<GameSessionView>({
    sessionId: credentials?.sessionId ?? '',
    guestToken: credentials?.guestToken,
    enabled: Boolean(credentials),
    onSnapshot: (snapshot) =>
      queryClient.setQueryData<GameSessionView>(key, (current) => newerSnapshot(current, snapshot)),
  });
  const form = useForm({
    initialValues: { displayName: '' },
    validate: { displayName: (value) => value.trim().length < 2 ? 'Введите не меньше двух символов' : null },
  });
  const join = useMutation({
    mutationFn: (displayName: string) => joinGameSession(token, { displayName }),
    onSuccess: (result) => {
      saveGuestCredentials(token, result.credentials);
      setNotice(null);
      setCredentials(result.credentials);
      queryClient.setQueryData(['guest-game-session', result.credentials.sessionId], result.session);
    },
    onError: (error) => notifications.show({ color: 'red', message: toUserMessage(error) }),
  });
  /*
    ВСЕ хуки объявляются здесь, до первого return.
    
    Ниже по коду три ранних выхода: пока гость не назвал имя, пока грузится
    снимок и когда доступ недействителен. Хук, объявленный после любого из них,
    вызывается не на каждом рендере: до входа участника React насчитает на один
    меньше, чем после, и уронит страницу с ошибкой #310 — «Rendered more hooks
    than during the previous render». Ровно так и случилось с grantedTalent:
    участник вводил имя, сервер отвечал успехом, и белый экран показывался
    именно в момент перехода к состоянию подключённого.
  */
  const commit = (snapshot: GameSessionView) =>
    queryClient.setQueryData<GameSessionView>(key, (current) => newerSnapshot(current, snapshot));
  const fail = (error: unknown) => notifications.show({ color: 'red', message: toUserMessage(error) });
  const roll = useMutation({ mutationFn: () => rollGuestGameDice(credentials!), onSuccess: commit, onError: fail });
  /* GAME-31: участник берёт свой талант от рождения сам. */
  const startTalent = useMutation({
    mutationFn: () => drawGuestStartTalent(credentials!),
    onSuccess: commit,
    onError: fail,
  });
  /* GAME-36: участник сам тянет карту за полученное на Базаре разрешение. */
  const grantedTalent = useMutation({
    mutationFn: () => drawGuestGrantedTalent(credentials!),
    onSuccess: commit,
    onError: fail,
  });
  const draw = useMutation({ mutationFn: () => drawGuestGameCard(credentials!), onSuccess: commit, onError: fail });
  const reveal = useMutation({
    mutationFn: (cardId: string) => revealGuestGameCard(credentials!, cardId),
    onSuccess: commit,
    onError: fail,
  });

  /** Забыть участника этого приглашения и вернуть форму имени. */
  const leave = (reason: string | null) => {
    forgetGuestCredentials(token);
    if (credentials) queryClient.removeQueries({ queryKey: ['guest-game-session', credentials.sessionId] });
    setCredentials(null);
    setNotice(reason);
  };

  /*
    Запомненный игрок больше не действует — ведущий удалил его или партии нет.
    Стираем только на такой ответ сервера: при обрыве связи игрок остаётся
    прежним и вернётся к своему ходу.
  */
  const revoked = query.isError && isRevokedGuestAccess(query.error);
  useEffect(() => {
    if (revoked) leave('Прежнее участие в этой партии больше не действует. Войдите заново.');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- реагируем только на смену признака
  }, [revoked]);

  if (!credentials) {
    /*
      Вход закрывает ведущий, а не старт партии: войти можно и в идущую игру,
      опоздавший встаёт в конец очереди хода.
    */
    const closed = !invitation.entryOpen;
    return (
      <GuestFrame>
        <Paper withBorder p="xl" radius="lg">
          <Stack gap="lg">
            <div>
              <Badge variant="light" color="gold" mb="sm">
                {invitation.game.variant === 'demo' ? 'Ознакомительная партия' : 'Игровая сессия'}
              </Badge>
              <Title order={1}>{invitation.title}</Title>
              <Text c="var(--text-muted)" mt="xs">{invitation.game.description}</Text>
              <Text size="sm" mt="sm">Ведущий: {invitation.hostDisplayName}</Text>
            </div>
            {notice ? (
              <Alert color="gold" icon={<IconAlertCircle size={18} />} data-testid="guest-notice">
                {notice}
              </Alert>
            ) : null}
            {closed ? (
              <Alert color="gold" icon={<IconAlertCircle size={18} />} title="Вход закрыт">
                {invitation.status === 'completed'
                  ? 'Эта партия уже завершена.'
                  : 'Ведущий закрыл подключение к этой комнате.'}
              </Alert>
            ) : (
              <form onSubmit={form.onSubmit((values) => join.mutate(values.displayName.trim()))}>
                <Stack>
                  <TextInput
                    label="Как вас показать участникам"
                    placeholder="Ваше имя"
                    data-testid="guest-name"
                    autoFocus
                    {...form.getInputProps('displayName')}
                  />
                  <Button type="submit" loading={join.isPending} leftSection={<IconLogin2 size={17} />}>
                    Присоединиться
                  </Button>
                </Stack>
              </form>
            )}
          </Stack>
        </Paper>
      </GuestFrame>
    );
  }

  if (query.isLoading || revoked) return <LoadingState label="Восстанавливаем партию..." />;
  if (query.isError || !query.data) {
    /* Не отказ в доступе, а обрыв связи или сбой: игрок не забывается. */
    return (
      <GuestFrame>
        <ErrorState
          title="Нет связи с сервером"
          message={`${toUserMessage(query.error)} Вы остаётесь игроком «${credentials.displayName}».`}
          onRetry={() => void query.refetch()}
        />
      </GuestFrame>
    );
  }
  const session = query.data;
  const busy = roll.isPending || draw.isPending || reveal.isPending ||
    startTalent.isPending || grantedTalent.isPending;
  return (
    <GuestFrame wide>
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="sm" wrap="nowrap" mb={2}>
              <GuestBrand />
              <Text tt="uppercase" size="xs" fw={700} c="var(--accent)">Игровая сессия</Text>
            </Group>
            <Title order={1}>{session.game.title}</Title>
            <Group gap="xs" mt="xs">
              <SessionStatusBadge status={session.status} />
              <Text size="sm" c="var(--text-muted)" data-testid="guest-display-name">{credentials.displayName}</Text>
              <SwitchGuestButton displayName={credentials.displayName} onConfirm={() => leave(null)} />
            </Group>
          </div>
          <Badge
            color={realtime === 'connected' ? 'teal' : 'gold'}
            leftSection={realtime === 'connected' ? <IconWifi size={13} /> : <IconWifiOff size={13} />}
          >
            {realtime === 'connected' ? 'В сети' : 'Переподключение'}
          </Badge>
        </Group>
        {session.status === 'completed' ? (
          <Alert color="teal" title="Партия завершена">
            Спасибо за участие. Поле, ваши карты и ресурсы остаются на этой странице для просмотра.
          </Alert>
        ) : null}
        {/*
          После финиша стол не исчезает: когда последний доходит до Маяка, экран
          у всех разом пустел. Ходить уже нельзя — стол сам закрыт вне активной партии.
        */}
        {session.status === 'waiting' ? (
          <>
            <SessionParticipants
              participants={roster(session).people}
              count={roster(session).seats}
              countLabel={PLAYERS_COUNT_LABEL}
            />
            <Alert color="teal" title="Вы подключились">
              Ждём старта ведущего. Ваш номер хода назначен по порядку входа.
            </Alert>
          </>
        ) : (
          <GameWorkspace
            session={session}
            viewerParticipantId={credentials.participantId}
            facilitator={false}
            busy={busy}
            /* В демо гость видит свой шаг и итог; пояснений ведущего ему не показывают. */
            demoViewer={session.demo ? { role: 'guest', participantId: credentials.participantId } : undefined}
            coachKey="creativitas.demo-coach.guest"
            connection={realtime}
            actions={{
              /* Сказку открывает и закрывает только ведущий — кнопок здесь нет. */
              startTalent: () => startTalent.mutate(),
              grantedTalent: () => grantedTalent.mutate(),
              roll: () => roll.mutate(),
              draw: () => draw.mutate(),
              reveal: (cardId) => reveal.mutate(cardId),
            }}
          />
        )}
      </Stack>
    </GuestFrame>
  );
}

/**
 * The platform wordmark, as the app shell draws it.
 *
 * A guest has no navigation to carry the brand, so the page shows it itself.
 * It used to sit on a line of its own above the title, which cost a screen's
 * worth of height on the board view — and it used `--font-serif`, a variable
 * that does not exist, so it silently rendered in the interface font while the
 * shell used the display one.
 */
function GuestBrand() {
  return (
    <Group gap={8} wrap="nowrap">
      <img src={BRAND.sunMark} alt="" width={20} height={20} />
      <Text fw={600} fz="md" style={{ fontFamily: 'var(--font-display)', letterSpacing: '0.01em' }}>
        {APP_NAME}
      </Text>
    </Group>
  );
}

function GuestFrame({ children, wide = false }: { children: React.ReactNode; wide?: boolean }) {
  return (
    <Container size={wide ? SESSION_SURFACE_WIDTH : 'sm'} py={{ base: 32, md: 64 }}>
      {/* On the board the brand rides the eyebrow line instead — see below. */}
      {wide ? null : (
        <Group mb="xl">
          <GuestBrand />
        </Group>
      )}
      {children}
    </Container>
  );
}
